/**
 * @quotech/ui/kanban — KanbanApi write-concurrency opts + typed errors.
 *
 * Split out of the former monolith `kanban/api.ts` (operator 500-line
 * ceiling). Re-exported verbatim via `kanban/api/index.ts`, so every
 * `from '.../api'` / `@quotech/ui/kanban` import is unchanged.
 */

import type { Card } from '../types'

/**
 * Optimistic-concurrency-control opts for write endpoints (Arc 5).
 *
 * Pass ``expectedUpdatedAt`` carrying the ``Card.updated_at`` value
 * the caller most recently observed (typically from the cached row
 * being edited). The BE compares to the live row; if changed, the
 * write is rejected with a 412 — surfaced to JS as
 * ``KanbanCardConflictError`` so callers can ``catch`` it and prompt
 * the operator.
 *
 * Omit to keep the legacy last-writer-wins behaviour (back-compat).
 * The opt-in is per-call so existing host bundles keep working
 * through the rollout window.
 */
export interface KanbanCasOpts {
  expectedUpdatedAt?: string | null
}

/**
 * Thrown by ``updateCard`` / ``moveCard`` when the BE returns 412
 * because the caller's ``expectedUpdatedAt`` was stale (a peer
 * updated the card since the caller last read it). Carries the
 * server's current snapshot so the FE can refresh the drawer
 * without a separate GET round-trip and offer a "discard / overwrite"
 * choice to the operator.
 */
export class KanbanCardConflictError extends Error {
  readonly current: Card | null
  readonly currentUpdatedAt: string | null
  readonly expectedUpdatedAt: string | null
  constructor(
    message: string,
    current: Card | null,
    currentUpdatedAt: string | null,
    expectedUpdatedAt: string | null,
  ) {
    super(message)
    this.name = 'KanbanCardConflictError'
    this.current = current
    this.currentUpdatedAt = currentUpdatedAt
    this.expectedUpdatedAt = expectedUpdatedAt
  }
}

/**
 * Thrown when a Kanban request never reached the server — the browser's
 * native ``fetch()`` rejects with ``TypeError: Failed to fetch`` on a
 * network-level failure (offline, DNS, CORS, blocked, server down, or a
 * silent token-refresh that itself couldn't reach the auth host). That
 * raw error used to propagate verbatim, so the card drawer showed a
 * cryptic "Save failed → Failed to fetch" with no recovery.
 *
 * Surfacing it as a typed error lets call-sites distinguish a transient
 * connectivity problem (offer Retry, keep the edit, auto-retry on
 * reconnect) from a real 4xx/5xx rejection. ``status`` is ``0`` — the
 * conventional "no HTTP response" marker — so toast styling and retry
 * logic can branch on it without string-matching.
 */
export class KanbanNetworkError extends Error {
  readonly status = 0
  /** The original ``TypeError`` (or other) the fetch layer caught. */
  readonly cause?: unknown
  constructor(message?: string, cause?: unknown) {
    super(
      message ||
        "Couldn't reach the server — check your connection. Your change is kept and will retry.",
    )
    this.name = 'KanbanNetworkError'
    this.cause = cause
  }
}

/** True for an error that represents a never-reached-server condition. */
export function isKanbanNetworkError(e: unknown): e is KanbanNetworkError {
  return e instanceof KanbanNetworkError
}
